import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { Form } from "@components";
import {
  AnnouncementModel,
  AnnouncementModelForForm,
} from "@api/types/announcements";
import { Manager } from "@components/form/state/types";
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { postAnnouncement, putAnnouncement } from "@api/admin/announcement";
import { alertService } from "@services";
import { useAnnouncementStore } from "../../../../../../store/hooksy";
import convertToHtml from "draftjs-to-html";
import { convertToRaw, EditorState } from "draft-js";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<AnnouncementModelForForm>;
}

type Key = keyof AnnouncementModelForForm;

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const editorConfiguration = {
  toolbar: [
    "heading",
    "|",
    "bold",
    "italic",
    "blockQuote",
    "link",
    "numberedList",
    "bulletedList",
    "|",
    "undo",
    "redo",
  ],
  placeholder: "Enter announcement message",
};

export const AnnouncementDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setAnnouncementHooksyStore] = useAnnouncementStore();

  let announcementModel: AnnouncementModel = {
    id: 0,
    title: "",
    message: "",
    datePosted: null,
    postedBy: "",
    isActive: false,
  };

  function clearModelAndClosePopup() {
    announcementModel.id = 0;
    announcementModel.title = "";
    announcementModel.message = "";
    announcementModel.datePosted = null;
    announcementModel.postedBy = "";
    announcementModel.isActive = false;
    form.reset();
    onCancel();
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "title":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "message":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value.getCurrentContent().getPlainText()),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          sendPayload();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function sendPayload() {
    announcementModel.id = form.values.id;
    announcementModel.title = form.values.title;
    announcementModel.message = convertToHtml(
      convertToRaw(form.values.message.getCurrentContent())
    );
    announcementModel.datePosted = form.values.datePosted;
    announcementModel.postedBy = form.values.postedBy;
    announcementModel.isActive = form.values.isActive;
    // creating new announcement
    if (announcementModel.id === 0) {
      createAnnouncement();
    }
    // updating existing announcement
    else {
      updateAnnouncement();
      // if (
      //   announcementModel.title !== form.values.title ||
      //   announcementModel.message !== form.values.message ||
      //   announcementModel.isActive !== form.values.isActive
      // ) {
      //   updateAnnouncement();
      // } else {
      //   clearModelAndClosePopup();
      // }
    }
  }

  function createAnnouncement() {
    setIsSubmitting(true);
    postAnnouncement(announcementModel)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Announcement successfully created." });
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({
            msg: "Please verify that you have the correct permissions.",
          });
        }
      })
      .then(() => {
        setIsSubmitting(false);
        clearModelAndClosePopup();
        setAnnouncementHooksyStore({ fetchList: true });
      });
  }

  function updateAnnouncement() {
    setIsSubmitting(true);
    putAnnouncement(announcementModel)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Announcement successfully updated." });
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({
            msg: "Please verify that you have the correct permissions.",
          });
        }
      })
      .then(() => {
        setIsSubmitting(false);
        clearModelAndClosePopup();
        setAnnouncementHooksyStore({ fetchList: true });
      });
  }

  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  return (
    <Box mt={2}>
      <Dialog open={open} maxWidth="sm" fullWidth disableEnforceFocus>
        <DialogTitle
          id="form-dialog-title"
          onClose={() => clearModelAndClosePopup()}
        >
          {`${modalTitle} Announcement`}
        </DialogTitle>
        <DialogContent>
          <Form>
            <Form.Textbox
              label="Title"
              name="title"
              onChange={onChange}
              size={12}
              error={form.errors.title}
              value={form.values.title}
              placeholder="Enter announcement title"
              disabled={isSubmitting}
              required
            />
            <Form.RichTextEditorForAnnouncements
              label="Message"
              name="message"
              required
              size={12}
              value={form.values.message}
              error={form.errors.message}
              onChange={onChange}
              disabled={isSubmitting}
            />
            <Form.Checkbox
              label="Is Active"
              name="isActive"
              size={12}
              value={form.values.isActive}
              onChange={onChange}
              disabled={isSubmitting}
            />
            <Box textAlign="right" mt={4} mb={4}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => clearModelAndClosePopup()}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="button"
                onClick={handleSubmit}
                style={{ marginLeft: "40px" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={20} /> : "Submit"}
              </Button>
            </Box>
          </Form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
